import { t } from "@airange/shared/i18n-admin-controls";
import type { MenuProps } from "antd";
import { App, Button, Dropdown, Popconfirm, Space } from "antd";
import { Fragment } from "react";
import { openAdminConfirm } from "./admin-action-confirm";
import {
  AdminActionIcon,
  renderAdminActionIcon,
  resolveAdminActionButtonVisual,
  shouldUseColoredActionIcon,
} from "./admin-action-icons";
import type { AdminPageActionItem, AdminResolvedActionButtonVisual } from "./admin-action-types";

export function AdminPageActions({ actions }: { actions: AdminPageActionItem[] }) {
  const { modal } = App.useApp();
  const normalizedActions = actions.filter(
    (action) =>
      !action.hidden && (action.node || action.overlay || action.onClick || action.confirm),
  );
  if (normalizedActions.length === 0) return null;

  const passthroughActions = normalizedActions.filter((action) => action.node || action.overlay);
  const buttonActions = normalizedActions.filter((action) => !action.node && !action.overlay);
  const visibleButtonActions = buttonActions.slice(0, 2);
  // Keep an explicitly primary/solid page action visible instead of burying it in the overflow menu.
  if (
    buttonActions.length > visibleButtonActions.length &&
    !visibleButtonActions.some((action) => action.color === "primary" || action.variant === "solid")
  ) {
    const promotedPrimary = buttonActions.find(
      (action) => action.color === "primary" || action.variant === "solid",
    );
    if (promotedPrimary) {
      const replacedAction = visibleButtonActions[0];
      visibleButtonActions[0] = promotedPrimary;
      if (visibleButtonActions[1]?.key === promotedPrimary.key && replacedAction) {
        visibleButtonActions[1] = replacedAction;
      }
    }
  }
  const visibleActionKeySet = new Set(visibleButtonActions.map((action) => action.key));
  const moreButtonActions = buttonActions.filter((action) => !visibleActionKeySet.has(action.key));

  const renderPageButton = (action: AdminPageActionItem) => {
    const buttonVisual = resolveAdminActionButtonVisual(action);
    const button = (
      <Button
        color={buttonVisual.color}
        variant={buttonVisual.variant}
        icon={renderAdminActionIcon(action, { colored: shouldUseColoredActionIcon(buttonVisual) })}
        disabled={Boolean(action.disabled)}
        loading={Boolean(action.loading)}
        danger={buttonVisual.danger}
        onClick={action.confirm ? undefined : action.onClick}
      >
        {action.label}
      </Button>
    );
    if (!action.confirm) return button;
    return (
      <Popconfirm
        title={action.confirm.title}
        description={action.confirm.content}
        okText={action.confirm.okText}
        cancelText={t("common.actions.cancel")}
        okButtonProps={{
          danger: Boolean(action.confirm.okDanger ?? action.danger),
          loading: Boolean(action.loading),
        }}
        disabled={Boolean(action.disabled)}
        onConfirm={() => action.onClick?.()}
      >
        {button}
      </Popconfirm>
    );
  };

  const moreItems: NonNullable<MenuProps["items"]> = moreButtonActions.map((action) => ({
    key: action.key,
    label: action.label,
    icon: renderAdminActionIcon(action),
    danger: Boolean(action.danger),
    disabled: Boolean(action.disabled || action.loading),
    onClick: () => {
      if (action.confirm) {
        openAdminConfirm(modal, {
          title: action.confirm.title,
          content: action.confirm.content,
          okText: action.confirm.okText,
          cancelText: t("common.actions.cancel"),
          okDanger: action.confirm.okDanger ?? action.danger,
          onOk: action.onClick,
        });
        return;
      }
      action.onClick?.();
    },
  }));

  return (
    <Space size={[8, 8]} wrap>
      {passthroughActions.map((action) => (
        <Fragment key={action.key}>{action.node ?? action.overlay}</Fragment>
      ))}
      {visibleButtonActions.map((action) => (
        <Fragment key={action.key}>{renderPageButton(action)}</Fragment>
      ))}
      {moreItems.length > 0 ? (
        <Dropdown menu={{ items: moreItems }} trigger={["click"]}>
          <Button icon={<AdminActionIcon name="MoreOutlined" />}>{t("common.actions.more")}</Button>
        </Dropdown>
      ) : null}
    </Space>
  );
}

export function AdminListToolbarActions({ actions }: { actions: AdminPageActionItem[] }) {
  const { modal } = App.useApp();
  const normalizedActions = actions.filter(
    (action) =>
      !action.hidden && (action.node || action.overlay || action.onClick || action.confirm),
  );
  if (normalizedActions.length === 0) return null;

  const passthroughActions = normalizedActions.filter((action) => action.node || action.overlay);
  const buttonActions = normalizedActions.filter((action) => !action.node && !action.overlay);
  const maxVisibleButtons = 4;
  const visibleButtonActions = buttonActions.slice(0, maxVisibleButtons);
  const visibleActionKeySet = new Set(visibleButtonActions.map((action) => action.key));
  const moreButtonActions = buttonActions.filter((action) => !visibleActionKeySet.has(action.key));

  const resolveToolbarButtonVisual = (
    action: AdminPageActionItem,
    index: number,
  ): AdminResolvedActionButtonVisual => {
    if (action.color || action.variant || action.danger) {
      return {
        color: action.color ?? (action.danger ? "danger" : "default"),
        variant: action.variant ?? (action.danger ? "outlined" : "outlined"),
        danger: Boolean(action.danger),
      };
    }
    if (index === 0) {
      return { color: "primary", variant: "solid", danger: false };
    }
    return { color: "default", variant: "outlined", danger: false };
  };

  const renderToolbarButton = (action: AdminPageActionItem, index: number) => {
    const buttonVisual = resolveToolbarButtonVisual(action, index);
    const explicitIcon = action.icon;
    const button = (
      <Button
        color={buttonVisual.color}
        variant={buttonVisual.variant}
        icon={explicitIcon}
        disabled={Boolean(action.disabled)}
        loading={Boolean(action.loading)}
        danger={buttonVisual.danger}
        onClick={action.confirm ? undefined : action.onClick}
      >
        {action.label}
      </Button>
    );
    if (!action.confirm) return button;
    return (
      <Popconfirm
        title={action.confirm.title}
        description={action.confirm.content}
        okText={action.confirm.okText}
        cancelText={t("common.actions.cancel")}
        okButtonProps={{
          danger: Boolean(action.confirm.okDanger ?? action.danger),
          loading: Boolean(action.loading),
        }}
        disabled={Boolean(action.disabled)}
        onConfirm={() => action.onClick?.()}
      >
        {button}
      </Popconfirm>
    );
  };

  const moreItems: NonNullable<MenuProps["items"]> = moreButtonActions.map((action) => ({
    key: action.key,
    label: action.label,
    icon: action.icon,
    danger: Boolean(action.danger),
    disabled: Boolean(action.disabled || action.loading),
    onClick: () => {
      if (action.confirm) {
        openAdminConfirm(modal, {
          title: action.confirm.title,
          content: action.confirm.content,
          okText: action.confirm.okText,
          cancelText: t("common.actions.cancel"),
          okDanger: action.confirm.okDanger ?? action.danger,
          onOk: action.onClick,
        });
        return;
      }
      action.onClick?.();
    },
  }));

  return (
    <Space size={[8, 8]} wrap>
      {passthroughActions.map((action) => (
        <Fragment key={action.key}>{action.node ?? action.overlay}</Fragment>
      ))}
      {visibleButtonActions.map((action, index) => (
        <Fragment key={action.key}>{renderToolbarButton(action, index)}</Fragment>
      ))}
      {moreItems.length > 0 ? (
        <Dropdown menu={{ items: moreItems }} trigger={["click"]}>
          <Button>{t("common.actions.more")}</Button>
        </Dropdown>
      ) : null}
    </Space>
  );
}

export function buildAdminTableToolBarRender(actions: AdminPageActionItem[]) {
  return () => [
    <Fragment key="table-actions">
      <AdminPageActions actions={actions} />
    </Fragment>,
  ];
}
